// RealityAI Agent API client — PHASE K7 (Web Agent UI).
// Consumes the existing K6 Agent API only (AGENT_API.md). No new endpoints.
//
// K6 contract:
//   GET    /api/agents?scope=standalone|project[&project_id]
//   POST   /api/agents            → 201
//   GET    /api/agents/{id}
//   PATCH  /api/agents/{id}
//   DELETE /api/agents/{id}       → 204
//   POST   /api/agents/{id}/runs  → 202 {run_id, status, events_url}
//   GET    /api/agents/{id}/runs[?status&limit&offset]
//   GET    /api/agent-runs/{id}
//   POST   /api/agent-runs/{id}/cancel
//
// K6-4 flag (agents_enabled=false) → every route returns 404 (no disclosure).
// The UI treats a 404 on list/get as the disabled/ComingSoon state (OQ-2).
//
// 429 contract (K5): body {detail, code, retry_after} + Retry-After header.
// 501 contract: body {detail, code} (resume / approvals not available in MVP).

import { request, ApiError, getToken } from "@/lib/api";

// ---------- Types (mirror backend/app/schemas/agent.py) ----------

export interface Agent {
  id: string;
  user_id: string;
  project_id: string | null;
  name: string;
  description: string | null;
  instructions: string;
  model_policy: Record<string, unknown>;
  tool_policy: Record<string, unknown>;
  memory_policy: Record<string, unknown>;
  status: "active" | "archived";
  created_at: string;
  updated_at: string;
}

export interface AgentCreate {
  name: string;
  description?: string | null;
  instructions?: string;
  project_id?: string | null;
  model_policy?: Record<string, unknown>;
  tool_policy?: Record<string, unknown>;
  memory_policy?: Record<string, unknown>;
}

export interface AgentUpdate {
  name?: string;
  description?: string | null;
  instructions?: string;
  project_id?: string | null;
  status?: "active" | "archived";
  model_policy?: Record<string, unknown> | null;
  tool_policy?: Record<string, unknown> | null;
  memory_policy?: Record<string, unknown> | null;
}

export type RunStatus =
  | "queued"
  | "running"
  | "waiting_approval"
  | "paused"
  | "completed"
  | "failed"
  | "cancelled";

/** Terminal statuses (K3 RunStatus / events_contract TERMINAL_EVENTS). */
export const TERMINAL_RUN_STATUSES: ReadonlySet<RunStatus> = new Set([
  "completed",
  "failed",
  "cancelled",
]);

export function isTerminalStatus(status: string | null | undefined): boolean {
  return !!status && TERMINAL_RUN_STATUSES.has(status as RunStatus);
}

export interface AgentRun {
  id: string;
  agent_id: string;
  agent_name: string;
  project_id: string | null;
  task: string;
  status: RunStatus;
  current_step: number;
  started_at: string | null;
  finished_at: string | null;
  duration_seconds: number | null;
  usage: Record<string, unknown>;
  result: string | null;
  error: { code?: string; message?: string } | null;
  created_at: string;
}

export interface RunCreateResult {
  run_id: string;
  status: string;
  events_url: string;
}

// ---------- Error helpers (K7 error UX: 404/403/409/429/501) ----------

// The K5 429 body carries a machine `code` and a `retry_after` (seconds).
// ApiError carries only status+message, so the client re-reads nothing —
// instead these helpers parse the ApiError.message (which holds `detail`)
// plus the structured fields when present (see `isAgentApiError` usage in
// components: they call the raw endpoints through `request` and inspect
// the thrown ApiError; for 429/501 we parse the JSON detail string).
export interface AgentErrorInfo {
  status: number;
  code?: string;
  retryAfter?: number;
  detail: string;
}

/**
 * Normalize a thrown error into AgentErrorInfo. For 429 the K5 body is
 * `{"detail": "...", "code": "...", "retry_after": N}` — `request()` keeps
 * the full parsed body on `ApiError.body` (K7), so the structured fields
 * are read directly; a JSON-stringified `detail` is the fallback.
 */
export function agentErrorInfo(e: unknown): AgentErrorInfo {
  if (e instanceof ApiError) {
    const detail = e.message;
    let code: string | undefined;
    let retryAfter: number | undefined;
    const body = (e.body ?? null) as Record<string, unknown> | null;
    if (body && typeof body === "object") {
      if (typeof body.code === "string") code = body.code;
      if (typeof body.retry_after === "number") retryAfter = body.retry_after;
    }
    if (code === undefined || retryAfter === undefined) {
      // Fallback: detail may itself be the JSON-stringified body.
      try {
        const parsed = JSON.parse(detail) as Record<string, unknown>;
        if (parsed && typeof parsed === "object") {
          code ??= typeof parsed.code === "string" ? parsed.code : undefined;
          retryAfter ??=
            typeof parsed.retry_after === "number" ? parsed.retry_after : undefined;
        }
      } catch {
        /* detail is a plain string — no structured fields */
      }
    }
    return { status: e.status, code, retryAfter, detail };
  }
  return { status: 0, detail: e instanceof Error ? e.message : String(e) };
}

/** 404 = agents disabled (K6-4) or foreign/nonexistent id (anti-enumeration). */
export function isAgentsDisabledError(e: unknown): boolean {
  return e instanceof ApiError && e.status === 404;
}

// ---------- Agents CRUD ----------

export type AgentScope = "standalone" | "project";

export async function fetchAgents(opts?: {
  scope?: AgentScope;
  projectId?: string;
}): Promise<Agent[]> {
  const params = new URLSearchParams();
  if (opts?.scope) params.set("scope", opts.scope);
  if (opts?.projectId) params.set("project_id", opts.projectId);
  const qs = params.toString();
  return request<Agent[]>(`/api/agents${qs ? `?${qs}` : ""}`);
}

export async function createAgent(data: AgentCreate): Promise<Agent> {
  return request<Agent>("/api/agents", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function fetchAgent(agentId: string): Promise<Agent> {
  return request<Agent>(`/api/agents/${agentId}`);
}

export async function updateAgent(
  agentId: string,
  data: AgentUpdate,
): Promise<Agent> {
  return request<Agent>(`/api/agents/${agentId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteAgent(agentId: string): Promise<void> {
  // K6 returns 204 No Content (empty body) — the shared `request()` helper
  // always calls res.json(), which throws on an empty body. Handle the
  // 2xx-no-body case explicitly (mirrors api.ts uploadFile's own fetch).
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`/api/agents/${agentId}`, { method: "DELETE", headers });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const data = (await res.json()) as { detail?: unknown };
      if (data.detail) detail = typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail);
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, detail);
  }
}

// ---------- Runs ----------

export async function createRun(
  agentId: string,
  task: string,
  model?: string,
): Promise<RunCreateResult> {
  return request<RunCreateResult>(`/api/agents/${agentId}/runs`, {
    method: "POST",
    body: JSON.stringify({ task, model: model || null }),
  });
}

export async function fetchAgentRuns(
  agentId: string,
  opts?: { status?: string; limit?: number; offset?: number },
): Promise<AgentRun[]> {
  const params = new URLSearchParams();
  if (opts?.status) params.set("status", opts.status);
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  if (opts?.offset != null) params.set("offset", String(opts.offset));
  const qs = params.toString();
  return request<AgentRun[]>(`/api/agents/${agentId}/runs${qs ? `?${qs}` : ""}`);
}

export async function fetchAgentRun(runId: string): Promise<AgentRun> {
  return request<AgentRun>(`/api/agent-runs/${runId}`);
}

export async function cancelAgentRun(runId: string): Promise<AgentRun> {
  // 409 when the run is already terminal (K3 owns terminal status). The UI
  // never infers terminal state from this response alone — the SSE terminal
  // event is authoritative (K6 known limitation #3: queued cancel race).
  return request<AgentRun>(`/api/agent-runs/${runId}/cancel`, { method: "POST" });
}

/** SSE endpoint path for a run (consumed by the K7 SSE client). */
export function agentRunEventsUrl(runId: string): string {
  return `/api/agent-runs/${runId}/events`;
}
